/**
 * DataGrid for string registry list (Config Registry–style)
 */

import { useMemo, useState } from "react";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridActionsCellItem,
  GridRenderCellParams,
  GridPaginationModel,
  GridColumnVisibilityModel,
  GridDensity,
} from "@mui/x-data-grid-pro";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import { useGlobalConfig } from "context/GlobalContext";
import {
  selectStringRegistryStrings,
  selectStringRegistryPagination,
  selectStringRegistryIsLoading
} from "../store/stringRegistryUISlice";
import { StringRegistryItem } from "../types/stringRegistryTypes";
import { getGlobalLocaleOverrides } from "../utils/globalTemplateLocales";
import { ConfigStatusBadge } from "features/config-registry/components/ConfigStatusBadge";
import { createColumnHeader } from "features/config-registry/components/ColumnHeader";

interface Props {
  onPageChange: (page: number, pageSize: number) => void;
  onRowClick?: (row: StringRegistryItem) => void;
  onEdit?: (row: StringRegistryItem) => void;
}

export const StringRegistryTable = ({ onPageChange, onRowClick, onEdit }: Props) => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const strings = useSelector(selectStringRegistryStrings);
  const pagination = useSelector(selectStringRegistryPagination);
  const isLoading = useSelector(selectStringRegistryIsLoading);
  const canEdit = rc.permissionsManager.canStringRegistryEdit();

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    // Visible by default
    stringResourceId: true,
    displayName: true,
    verificationStatus: true,
    locales: true,
    components: true,
    // Hidden by default — users can show via column menu
    sections: false,
    deprecated: false,
    createdAt: false,
    updatedAt: false,
  });

  const [density, setDensity] = useState<GridDensity>("standard");

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "serialNo",
        headerName: "#",
        width: 70,
        sortable: false,
        hideable: false,
        resizable: false,
        align: "center",
        headerAlign: "center",
        renderHeader: createColumnHeader("#", "serialNo"),
        renderCell: (params: GridRenderCellParams) => {
          const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
          const serialNo = (pagination.page - 1) * pagination.pageSize + rowIndex + 1;
          return (
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
              {serialNo}
            </Typography>
          );
        },
      },
      {
        field: "stringResourceId",
        headerName: "stringResourceId",
        flex: 1.4,
        minWidth: 220,
        hideable: false,
        resizable: true,
        renderHeader: createColumnHeader("stringResourceId", "stringResourceId"),
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value || ""} placement="top" arrow>
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85rem",
                color: theme.palette.primary.main,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.value}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "displayName",
        headerName: "Display Name",
        flex: 1.2,
        minWidth: 180,
        resizable: true,
        renderHeader: createColumnHeader("Display Name", "displayName"),
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value || ""} placement="top" arrow>
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.value}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "verificationStatus",
        headerName: "Status",
        width: 140,
        resizable: true,
        renderHeader: createColumnHeader("Status", "verificationStatus"),
        renderCell: (params: GridRenderCellParams) => (
          <ConfigStatusBadge status={params.value} />
        ),
      },
      {
        field: "locales",
        headerName: "Locales",
        flex: 1,
        minWidth: 140,
        resizable: true,
        valueGetter: (_v: any, row: StringRegistryItem) =>
          getGlobalLocaleOverrides(row).map((x) => x.locale).join(", "),
        renderHeader: createColumnHeader("Locales", "locales"),
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value || ""} placement="top" arrow>
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {params.value || <Typography variant="body2" color="text.disabled">—</Typography>}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "components",
        headerName: "Components",
        flex: 0.9,
        minWidth: 130,
        resizable: true,
        valueGetter: (_v: any, row: StringRegistryItem) =>
          (row.components || []).map((c) => c.code).join(", "),
        renderHeader: createColumnHeader("Components", "components"),
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value || ""} placement="top" arrow>
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {params.value || <Typography variant="body2" color="text.disabled">—</Typography>}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "sections",
        headerName: "Sections",
        flex: 0.9,
        minWidth: 130,
        resizable: true,
        valueGetter: (_v: any, row: StringRegistryItem) =>
          (row.sections || []).map((s) => s.code).join(", "),
        renderHeader: createColumnHeader("Sections", "sections"),
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={params.value || ""} placement="top" arrow>
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {params.value || <Typography variant="body2" color="text.disabled">—</Typography>}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "deprecated",
        headerName: "Deprecated",
        width: 120,
        resizable: true,
        type: "boolean",
        renderHeader: createColumnHeader("Deprecated", "deprecated"),
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {params.value ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 18, color: "warning.main" }} />
                <Typography variant="body2" color="warning.main">Yes</Typography>
              </>
            ) : (
              <>
                <CancelIcon sx={{ fontSize: 18, color: "success.main" }} />
                <Typography variant="body2" color="text.secondary">No</Typography>
              </>
            )}
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: "Created",
        width: 160,
        resizable: true,
        type: "dateTime",
        renderHeader: createColumnHeader("Created", "createdAt"),
        renderCell: (params: GridRenderCellParams) => {
          if (!params.value) return <Typography variant="body2" color="text.disabled">—</Typography>;
          const date = new Date(params.value);
          const formatted = date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            <Tooltip title={date.toLocaleString()} placement="top" arrow>
              <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                {formatted}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        width: 160,
        resizable: true,
        type: "dateTime",
        renderHeader: createColumnHeader("Updated", "updatedAt"),
        renderCell: (params: GridRenderCellParams) => {
          if (!params.value) return <Typography variant="body2" color="text.disabled">—</Typography>;
          const date = new Date(params.value);
          const formatted = date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            <Tooltip title={date.toLocaleString()} placement="top" arrow>
              <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                {formatted}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        pinnable: true,
        hideable: false,
        renderHeader: createColumnHeader("Actions", "actions"),
        cellClassName: "actions-column",
        headerClassName: "actions-column-header",
        getActions: (params) => {
          const row = params.row as StringRegistryItem;
          return [
            <GridActionsCellItem
              key="view"
              icon={<Tooltip title="View"><VisibilityIcon /></Tooltip>}
              label="View"
              onClick={() => onRowClick?.(row)}
            />,
            ...(canEdit
              ? [
                  <GridActionsCellItem
                    key="edit"
                    icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
                    label="Edit"
                    onClick={() => onEdit?.(row)}
                  />,
                ]
              : []),
          ];
        },
      },
    ],
    [theme, pagination.page, pagination.pageSize, canEdit, onRowClick, onEdit]
  );

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    onPageChange(model.page + 1, model.pageSize);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: "calc(100vh - 320px)", sm: "calc(100vh - 280px)" },
        minHeight: 400,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
            : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      }}
    >
      <DataGridPro
        rows={strings}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.documentId}

        // Pagination
        pagination
        paginationMode="server"
        rowCount={pagination.total}
        paginationModel={{
          page: Math.max(0, pagination.page - 1),
          pageSize: pagination.pageSize,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}

        // Row interaction
        disableRowSelectionOnClick
        onRowClick={(params) => onRowClick?.(params.row as StringRegistryItem)}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }

        // Column features
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}

        // Density
        density={density}
        onDensityChange={setDensity}

        // Initial state
        initialState={{
          pinnedColumns: { right: ["actions"] },
          columns: {
            columnVisibilityModel: {
              stringResourceId: true,
              displayName: true,
              verificationStatus: true,
              locales: true,
              components: true,
              sections: false,
              deprecated: false,
              createdAt: false,
              updatedAt: false,
            },
          },
        }}

        sx={{
          border: "none",
          bgcolor: "background.paper",
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: theme.palette.mode === "dark" ? "grey.800" : "#f8f9fa",
            borderBottom: `2px solid ${theme.palette.divider}`,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "0.8125rem",
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            },
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
            transition: "background-color 0.15s ease",
            "&.even-row": { bgcolor: "background.paper" },
            "&.odd-row": {
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.015)",
            },
            "&:hover": {
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(25, 118, 210, 0.12)"
                  : "rgba(25, 118, 210, 0.06)",
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.06)"
            }`,
            py: 1,
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            "&:focus, &:focus-within": { outline: "none" },
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: `2px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === "dark" ? "grey.800" : "#f8f9fa",
          },
          "& .MuiDataGrid-virtualScroller": { bgcolor: "background.paper" },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          "& .actions-column-header": {
            borderLeft: `2px solid ${theme.palette.divider}`,
          },
          "& .actions-column": {
            borderLeft: `2px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)"
            }`,
          },
          "& .MuiDataGrid-pinnedColumns": {
            boxShadow:
              theme.palette.mode === "dark"
                ? "-4px 0 8px -2px rgba(0, 0, 0, 0.5)"
                : "-4px 0 8px -2px rgba(0, 0, 0, 0.1)",
          },
          "& .MuiDataGrid-overlay": {
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(0, 0, 0, 0.5)"
                : "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(2px)",
          },
          "& .MuiDataGrid-overlayWrapper": { minHeight: 400 },
        }}
      />
    </Box>
  );
};

export default StringRegistryTable;
